import {
  CommandGroup,
  CommandInput,
  CommandList,
} from "@volca/ui/components/ui/command";
import { Stack } from "@volca/ui/components/ui/stack";
import type { ApJobSearchResult } from "@/accounts-payable/types";
import {
  jobSearchMeta,
  jobSearchStatusMessage,
} from "@/accounts-payable/utils";
import { PickerCommand, PickerItem } from "./picker-command";

export const JobSearchCombobox = ({
  query,
  onQueryChange,
  jobs,
  isLoading,
  isFetching,
  isError,
  isRateLimited,
  selectedJob,
  onSelect,
  disabled,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  jobs: ApJobSearchResult[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  /** ServiceTitan throttled the search — reported apart from a generic failure. */
  isRateLimited?: boolean;
  selectedJob: ApJobSearchResult | null;
  onSelect: (job: ApJobSearchResult) => void;
  disabled?: boolean;
}) => {
  const statusMessage = jobSearchStatusMessage({
    query,
    isLoading,
    isError,
    isRateLimited,
    resultCount: jobs.length,
  });

  return (
    <Stack className="w-full" align="start" gap={1}>
      <PickerCommand label="Search ServiceTitan jobs">
        <CommandInput
          value={query}
          onValueChange={onQueryChange}
          disabled={disabled}
          placeholder="Search by job number, customer, or address"
        />
        <CommandList className="max-h-64">
          {!statusMessage && (
            <CommandGroup>
              {jobs.map((job) => {
                const isSelected = selectedJob?.id === job.id;
                return (
                  <PickerItem
                    key={job.id}
                    value={String(job.id)}
                    selected={isSelected}
                    // Not just the input: an option still fires onSelect while
                    // the match request is in flight, which would move the
                    // checkmark and the confirm label off the job actually
                    // being submitted.
                    disabled={disabled}
                    onSelect={() => onSelect(job)}
                  >
                    <Stack align="start" gap={0} className="min-w-0 flex-1">
                      <Stack
                        direction="row"
                        gap={2}
                        align="baseline"
                        className="flex-wrap"
                      >
                        <span className="font-semibold">
                          Job {job.job_number}
                        </span>
                        <span
                          className="truncate text-sm text-subtle"
                          title={job.customer_name}
                        >
                          {job.customer_name}
                        </span>
                      </Stack>
                      {job.address && (
                        <span
                          className="truncate text-xs text-subtle"
                          title={job.address}
                        >
                          {job.address}
                        </span>
                      )}
                      <span className="text-xs text-subtle">
                        {jobSearchMeta(job).join(" · ")}
                      </span>
                    </Stack>
                  </PickerItem>
                );
              })}
            </CommandGroup>
          )}
        </CommandList>
        {statusMessage && (
          <Stack
            gap={4}
            align="center"
            role="status"
            className="w-full px-4 py-6 text-center text-sm text-subtle"
          >
            {statusMessage}
          </Stack>
        )}
      </PickerCommand>
      {isFetching && !statusMessage && (
        <span className="text-xs text-subtle">Searching jobs…</span>
      )}
    </Stack>
  );
};
